import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./Animation3DPages.css";

const pages = {
    character: {
        slug: "character-animation",
        eyebrow: "Blender Character Animation",
        title: "Game Character Animation That Feels Responsive, Expressive & Playable",
        description: "Oengines Games LLP creates Blender-based 3D character animation for game heroes, NPCs, enemies, avatars, cutscenes, combat, locomotion, emotes, and in-game actions.",
        keywords: "Blender character animation, game character animation, 3D character animation for games, combat animation, locomotion animation, game avatar animation",
        className: "character-layout",
        heroImage: "assets/img/GameDevelopment/Action/character.png",
        stats: ["Combat", "Locomotion", "Emotes", "NPCs"],
        layout: "action",
        spotlight: {
            title: "Playable Character Animation Sets",
            text: "Character animation is planned as a complete gameplay set, not only as isolated clips. We focus on input response, silhouette clarity, attack readability, blend timing, and loops that feel natural inside Unity or Unreal gameplay.",
            items: ["Responsive locomotion", "Readable combat poses", "Clean loop transitions", "Avatar emote polish"]
        },
        qualityChecks: ["Foot contact and weight shift", "Start and end pose matching", "Combat anticipation and recovery", "Blend tree friendly timing", "FBX/GLTF export review"],
        services: [
            ["Locomotion Sets", "Idle, walk, run, sprint, jump, fall, land, turn, crouch, climb, and traversal animation sets."],
            ["Combat Animation", "Attacks, combos, hit reactions, blocks, dodges, reloads, weapon actions, and ability animations."],
            ["NPC Behavior", "Ambient actions, patrols, gestures, reactions, interaction loops, and story-driven character states."],
            ["Game-Ready Export", "Clean Blender animation files, action clips, naming conventions, loop checks, and engine handoff notes."]
        ],
        pipeline: ["Reference study", "Blocking", "Spline polish", "Loop cleanup", "Game export"],
        deliverables: ["Idle cycles", "Run cycles", "Attack clips", "Hit reactions", "Emotes", "Blend-ready actions", "FBX/GLTF export", "Engine handoff"],
        tableTitle: "Character Animation Clip Plan",
        table: [
            ["Hero Movement", "Idle, walk, run, jump", "Gameplay"],
            ["Combat Set", "Attack, block, dodge, hit", "Action"],
            ["NPC Set", "Talk, gesture, react, patrol", "World"],
            ["Avatar Set", "Emotes, victory, defeat", "Social"]
        ],
        faqs: [
            ["Do you animate characters only in Blender?", "Yes. These 3D animation services are planned around Blender animation, cleanup, export, and game-engine handoff."],
            ["Can you create looping game character animations?", "Yes. We create clean loops for idle, walk, run, combat stance, emotes, and repeated gameplay actions."],
            ["Can animations be exported for Unity or Unreal?", "Yes. We can prepare FBX or GLTF exports and provide handoff notes for game implementation."]
        ]
    },
    creature: {
        slug: "creature-animation",
        eyebrow: "Blender Creature Animation",
        title: "Creature Animation for Monsters, Animals, Bosses & Fantasy Game Worlds",
        description: "We create Blender creature animation for monsters, animals, dragons, bosses, stylized creatures, enemy AI actions, attacks, movement, reactions, and cinematic moments.",
        keywords: "Blender creature animation, game creature animation, monster animation, animal animation for games, boss animation, fantasy creature animation",
        className: "creature-layout",
        heroImage: "assets/img/GameDevelopment/School-of-Dragons800x600.png",
        stats: ["Bosses", "Monsters", "Animals", "Fantasy"],
        layout: "wild",
        spotlight: {
            title: "Creature Behavior Animation System",
            text: "Creature animation needs strong body mechanics, threat timing, and personality. We build motion around AI states, boss phases, attack distance, hit feedback, and fantasy creature behavior that players can read during gameplay.",
            items: ["Creature silhouette study", "Attack distance timing", "AI state coverage", "Boss phase moments"]
        },
        qualityChecks: ["Weight and ground contact", "Wing, tail, claw or jaw timing", "Attack readability", "Navigation state consistency", "Boss phase transition polish"],
        services: [
            ["Creature Movement", "Walk, run, crawl, fly, swim, pounce, climb, roar, idle, and attack-ready movement cycles."],
            ["Boss Attack Sets", "Heavy attacks, charge moves, area effects, stagger states, rage mode, death, and victory moments."],
            ["AI State Animation", "Patrol, chase, alert, search, attack, retreat, sleep, wounded, and transition animation states."],
            ["Style Matching", "Cartoon, realistic, stylized, horror, fantasy, casual, mobile-friendly, and low-poly creature motion."]
        ],
        pipeline: ["Anatomy reference", "Motion blocking", "Weight timing", "Threat polish", "Engine export"],
        deliverables: ["Creature cycles", "Attack moves", "Roar clips", "Death clips", "Boss phases", "AI state actions", "Loop cleanup", "Export files"],
        tableTitle: "Creature Animation Behavior Plan",
        table: [
            ["Movement", "Walk, run, fly, crawl", "AI Navigation"],
            ["Combat", "Bite, claw, charge, roar", "Enemy Action"],
            ["Reactions", "Hit, stagger, flee, death", "Gameplay Feedback"],
            ["Boss Phase", "Intro, rage, defeat", "Cinematic"]
        ],
        faqs: [
            ["Can you animate fantasy creatures in Blender?", "Yes. We create Blender-based animation for monsters, animals, fantasy creatures, bosses, and stylized game enemies."],
            ["Can creature animations support AI behavior?", "Yes. We plan animations around patrol, chase, attack, alert, retreat, death, and other state-machine needs."],
            ["Can you create boss animation sets?", "Yes. We can create intro, idle, attack, rage phase, stagger, death, and cinematic clips for game bosses."]
        ]
    },
    rigging: {
        slug: "rigging-skinning",
        eyebrow: "Blender Rigging & Skinning",
        title: "Blender Rigging & Skinning for Game Characters and Creatures",
        description: "Oengines creates Blender rigs and skinning for characters, creatures, props, weapons, vehicles, avatars, NPCs, and game-ready animation systems.",
        keywords: "Blender rigging, Blender skinning, game character rigging, creature rigging, 3D rigging for games, game-ready rig",
        className: "rigging-layout",
        heroImage: "assets/img/GameDevelopment/3d-game-sprite1.png",
        stats: ["Rigs", "Weights", "Controls", "Export"],
        layout: "technical",
        spotlight: {
            title: "Blender Rig Lab for Game Assets",
            text: "Rigging pages should feel technical because this service directly affects animation speed and engine handoff. We create animator-friendly controls, clean deformation, predictable bone naming, and export-ready skeletons.",
            items: ["IK/FK control setup", "Deformation testing", "Engine-safe hierarchy", "Pose and export checks"]
        },
        qualityChecks: ["Bone hierarchy cleanup", "Shoulder, hip and knee deformation", "Control naming clarity", "Constraint export safety", "Retarget and clip test poses"],
        services: [
            ["Character Rigs", "Humanoid rigs with IK/FK, hand controls, spine controls, foot roll, facial hooks, and clean animation controls."],
            ["Creature Rigs", "Quadruped, winged, fantasy, boss, tail, tentacle, animal, and stylized creature rig structures."],
            ["Skin Weighting", "Clean deformation around shoulders, elbows, knees, hips, neck, face, fingers, and game-critical joints."],
            ["Game Export Setup", "Skeleton cleanup, bone naming, clip-friendly structure, engine-safe constraints, and FBX/GLTF export checks."]
        ],
        pipeline: ["Skeleton planning", "Control rig", "Skin weighting", "Deformation tests", "Export validation"],
        deliverables: ["Blender rig file", "IK/FK controls", "Skin weights", "Deformation tests", "Bone naming", "Export-ready skeleton", "Pose library", "Rig notes"],
        tableTitle: "Rigging & Skinning Checklist",
        table: [
            ["Skeleton", "Clean game-ready bone hierarchy", "Rig"],
            ["Controls", "IK/FK, hands, spine, feet", "Animator"],
            ["Weights", "Joint deformation checks", "Skinning"],
            ["Export", "Engine-safe FBX/GLTF setup", "Handoff"]
        ],
        faqs: [
            ["Do you rig only in Blender?", "Yes. We create rigs, controls, skin weights, and export-ready files using Blender."],
            ["Can you rig creatures and non-human characters?", "Yes. We can rig quadrupeds, fantasy creatures, monsters, bosses, tails, wings, and stylized forms."],
            ["Can rigs be prepared for game engines?", "Yes. We keep game export requirements in mind, including skeleton cleanup, deformation checks, and FBX/GLTF handoff."]
        ]
    },
    facial: {
        slug: "facial-animation",
        eyebrow: "Blender Facial Animation",
        title: "Facial Animation for Game Characters, Dialogues & Cinematic Moments",
        description: "We create Blender facial animation for expressive game characters, dialogue scenes, emotes, reactions, cutscenes, avatars, and stylized storytelling moments.",
        keywords: "Blender facial animation, game facial animation, character facial expressions, lip sync animation, game dialogue animation",
        className: "facial-layout",
        heroImage: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
        stats: ["Lip Sync", "Emotions", "Emotes", "Dialogue"],
        layout: "performance",
        spotlight: {
            title: "Expression Board for Story and Social Games",
            text: "Facial animation is built around emotion changes, mouth shapes, eye focus, and performance beats. The goal is to make close-ups, avatars, dialogue scenes, and lobby emotes feel alive without overcomplicating the game pipeline.",
            items: ["Mouth shape timing", "Eye and eyebrow acting", "Emote expression sets", "Dialogue performance beats"]
        },
        qualityChecks: ["Expression readability", "Lip sync timing", "Eye direction and blinks", "Head motion support", "Close-up polish pass"],
        services: [
            ["Expression Sets", "Happy, angry, sad, shocked, fear, confident, pain, victory, defeat, and stylized emotional poses."],
            ["Dialogue Animation", "Mouth shapes, timing, head movement, eye focus, eyebrow action, and performance polish for dialogue scenes."],
            ["Avatar Emotes", "Social reactions, profile expressions, lobby animations, victory poses, and collectible character moments."],
            ["Cinematic Faces", "Close-up performance, subtle eye movement, emotional beats, and Blender scene-ready facial animation."]
        ],
        pipeline: ["Expression plan", "Mouth shape setup", "Performance blocking", "Polish passes", "Export notes"],
        deliverables: ["Expression clips", "Lip sync clips", "Eye movement", "Eyebrow actions", "Emote faces", "Dialogue actions", "Blend shapes guidance", "Handoff notes"],
        tableTitle: "Facial Animation Coverage",
        table: [
            ["Dialogue", "Lip sync and head motion", "Story"],
            ["Emotion", "Mood and reactions", "Character"],
            ["Avatar", "Lobby and social emotes", "Retention"],
            ["Cinematic", "Close-up acting beats", "Cutscene"]
        ],
        faqs: [
            ["Can you create facial animation in Blender?", "Yes. We create Blender facial animation for expressions, lip sync, emotes, dialogue, and cinematic scenes."],
            ["Can facial animation be stylized?", "Yes. We can match stylized, cartoon, semi-realistic, fantasy, or mobile-friendly character expression styles."],
            ["Do you support dialogue scenes?", "Yes. We can animate mouth shapes, facial expressions, eye movement, head motion, and dialogue beats."]
        ]
    },
    mocap: {
        slug: "motion-capture-cleanup",
        eyebrow: "Blender Motion Capture Cleanup",
        title: "Motion Capture Cleanup for Game-Ready Blender Animation",
        description: "Oengines cleans and polishes motion capture animation in Blender for gameplay, cinematics, combat, sports, dance, character movement, and realistic game scenes.",
        keywords: "Blender motion capture cleanup, mocap cleanup for games, motion capture animation cleanup, game mocap cleanup, Blender mocap retargeting",
        className: "mocap-layout",
        heroImage: "assets/img/GameDevelopment/Sports-Booking-App-India.png",
        stats: ["Retarget", "Cleanup", "Polish", "Export"],
        layout: "cleanup",
        spotlight: {
            title: "Motion Capture Cleanup Review",
            text: "Raw capture often needs cleanup before it works in a game. We refine contacts, root motion, body pops, retargeting issues, and timing so captured movement feels intentional in gameplay and cinematics.",
            items: ["Foot slide cleanup", "Root motion notes", "Retargeting fixes", "Loopable gameplay clips"]
        },
        qualityChecks: ["Foot and hand contacts", "Jitter and pop removal", "Root motion consistency", "Character proportion fit", "Loop start and end cleanup"],
        services: [
            ["Mocap Cleanup", "Foot sliding, jitter, pops, timing, contact points, hand placement, root motion, and body mechanics cleanup."],
            ["Retargeting Support", "Transfer captured motion to Blender rigs, character proportions, game skeletons, and stylized characters."],
            ["Gameplay Adaptation", "Convert raw capture into loopable, responsive, gameplay-friendly clips for movement and actions."],
            ["Cinematic Polish", "Refine performance timing, weight, camera-facing poses, body language, and scene continuity."]
        ],
        pipeline: ["Import capture", "Retarget", "Clean contacts", "Polish motion", "Game export"],
        deliverables: ["Clean mocap clips", "Retargeted actions", "Loop cleanup", "Root motion notes", "Contact fixes", "Gameplay adaptation", "Cinematic polish", "Export files"],
        tableTitle: "Mocap Cleanup Focus Areas",
        table: [
            ["Foot Contacts", "Sliding and ground contact", "Movement"],
            ["Body Jitter", "Noise and pops", "Cleanup"],
            ["Retargeting", "Rig and proportion matching", "Pipeline"],
            ["Looping", "Game-ready repeated actions", "Gameplay"]
        ],
        faqs: [
            ["Can you clean motion capture in Blender?", "Yes. We clean mocap data in Blender for game-ready clips, cinematic scenes, and character movement."],
            ["Can mocap be retargeted to custom characters?", "Yes. We can retarget and adjust captured motion to fit different rigs, proportions, and game character styles."],
            ["Can mocap be made loopable for games?", "Yes. We can adapt mocap into loopable movement, idle, combat, and action clips where needed."]
        ]
    },
    cinematics: {
        slug: "game-cinematics",
        eyebrow: "Blender Game Cinematics",
        title: "Blender Cinematics for Game Trailers, Cutscenes & Story Moments",
        description: "We create Blender cinematics for game trailers, cutscenes, hero reveals, boss intros, environment shots, product showcases, and narrative moments.",
        keywords: "Blender game cinematics, game cutscene animation, game trailer animation, cinematic animation for games, Blender cutscenes",
        className: "cinematic-layout",
        heroImage: "assets/img/GameDevelopment/Battle-Royale-Game-th.png",
        stats: ["Trailers", "Cutscenes", "Boss Intros", "Story"],
        layout: "cinema",
        spotlight: {
            title: "Shot-Based Cinematic Production",
            text: "Cinematics need a stronger page layout because the work is driven by story beats, camera rhythm, staging, and marketing impact. We plan shots, scene blocking, camera motion, animation polish, and render handoff in Blender.",
            items: ["Storyboard and beat plan", "Camera movement", "Hero reveal shots", "Render handoff planning"]
        },
        qualityChecks: ["Shot composition", "Camera pacing", "Character staging", "Lighting direction notes", "Trailer or cutscene continuity"],
        services: [
            ["Game Trailers", "Hero shots, gameplay-style scenes, reveals, character moments, title cards, and marketing-ready cinematic pacing."],
            ["Cutscene Animation", "Story beats, character staging, camera animation, transitions, environmental action, and dramatic moments."],
            ["Boss & Level Intros", "Creature reveals, arena intros, mission starts, level transitions, and gameplay setup sequences."],
            ["Scene Polish", "Camera framing, animation timing, lighting direction, composition, and Blender render preparation."]
        ],
        pipeline: ["Script beats", "Storyboard", "Scene blocking", "Animation polish", "Render handoff"],
        deliverables: ["Storyboard notes", "Camera animation", "Character shots", "Scene blocking", "Cutscene clips", "Trailer beats", "Render-ready scene", "Exported video"],
        tableTitle: "Cinematic Production Plan",
        table: [
            ["Trailer", "Marketing hero shots", "Promotion"],
            ["Cutscene", "Story and character beats", "Narrative"],
            ["Intro", "Boss or level setup", "Gameplay"],
            ["Camera", "Framing and pacing", "Direction"]
        ],
        faqs: [
            ["Can you create game cinematics in Blender?", "Yes. We create Blender-based cinematic scenes, cutscenes, trailer shots, boss intros, and story moments."],
            ["Can cinematics be made for marketing?", "Yes. We can create hero shots, promotional sequences, product-style scenes, and trailer-ready animation beats."],
            ["Can you animate cameras and scenes?", "Yes. We handle camera movement, shot timing, scene staging, animation polish, and render preparation in Blender."]
        ]
    },
    product: {
        slug: "product-animations",
        eyebrow: "Blender Product Animations",
        title: "3D Product Animations for Game Assets, Props, Weapons & Digital Items",
        description: "Oengines creates Blender product animations for game props, weapons, vehicles, collectibles, NFTs, avatars, UI showcase assets, store items, and digital product reveals.",
        keywords: "Blender product animation, 3D product animation for games, game prop animation, weapon animation showcase, digital item animation",
        className: "product-layout",
        heroImage: "assets/img/GameDevelopment/Product_Poker.png",
        stats: ["Props", "Weapons", "Vehicles", "Items"],
        layout: "showcase",
        spotlight: {
            title: "Digital Item Showcase Animations",
            text: "Product animation for games should help players understand value. We create turntables, unlock moments, rarity reveals, reward loops, weapon inspections, and store previews that support monetization and live operations.",
            items: ["Store item previews", "Weapon reveal loops", "Rarity and reward moments", "Transparent render exports"]
        },
        qualityChecks: ["Readable product silhouette", "Loop and turntable timing", "Material and lighting polish", "Reward reveal pacing", "Store preview export checks"],
        services: [
            ["Game Asset Showcases", "Rotate, reveal, inspect, upgrade, unlock, rarity, and store presentation animations for digital game items."],
            ["Weapon & Prop Animation", "Weapon reveals, reload showcases, attachments, effects timing, collectible props, and item-focused animations."],
            ["Vehicle & Product Motion", "Vehicle spins, mechanical parts, feature reveals, product hero shots, and marketing-friendly animation loops."],
            ["Store & UI Motion", "Animated item previews, reward cards, chest openings, inventory moments, and game store presentation clips."]
        ],
        pipeline: ["Asset review", "Motion concept", "Lighting setup", "Animation polish", "Render/export"],
        deliverables: ["Product loops", "Reveal clips", "Turntables", "Store previews", "Upgrade animations", "Reward animations", "Render clips", "Transparent exports"],
        tableTitle: "Product Animation Uses",
        table: [
            ["Store Item", "Unlock and preview animation", "Monetization"],
            ["Weapon", "Reveal and inspect motion", "Game Asset"],
            ["Vehicle", "Feature and spin animation", "Showcase"],
            ["Reward", "Chest, card, rarity reveal", "LiveOps"]
        ],
        faqs: [
            ["Can you animate game products in Blender?", "Yes. We create Blender product animations for weapons, props, vehicles, collectibles, store items, and digital assets."],
            ["Can product animation be used in game stores?", "Yes. We can create item preview loops, unlock animations, reward reveals, and store showcase clips."],
            ["Can you export product animation videos?", "Yes. We can prepare rendered clips, transparent exports where applicable, and handoff files based on project needs."]
        ]
    }
};

function Animation3DPage({ page }) {
    const data = pages[page];
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.eyebrow,
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": data.eyebrow,
        "areaServed": "Worldwide",
        "description": data.description
    };

    return (
        <>
            <Helmet>
                <title>{data.title} | Oengines Games LLP</title>
                <meta name="description" content={data.description} />
                <meta name="keywords" content={data.keywords} />
                <link rel="canonical" href={`https://www.oengines.com/${data.slug}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className={`animation3d-page ${data.className} animation3d-layout-${data.layout}`}>
                <section className="animation3d-hero">
                    <div className="container">
                        <div className="animation3d-hero-grid">
                            <div>
                                <span className="animation3d-eyebrow">{data.eyebrow}</span>
                                <h1>{data.title}</h1>
                                <p>{data.description}</p>
                                <div className="animation3d-actions">
                                    <a href="/contact" className="as-btn">Discuss Animation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="animation3d-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="animation3d-stage">
                                <img src={data.heroImage} alt={data.title} />
                                <div className="animation3d-orbit">
                                    {data.stats.map((item) => <span key={item}>{item}</span>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className={`animation3d-spotlight-section animation3d-spotlight-${data.layout}`}>
                    <div className="container">
                        <div className="animation3d-spotlight-panel">
                            <div className="animation3d-spotlight-copy">
                                <span>Production Focus</span>
                                <h2>{data.spotlight.title}</h2>
                                <p>{data.spotlight.text}</p>
                            </div>
                            <div className="animation3d-spotlight-list">
                                {data.spotlight.items.map((item, index) => (
                                    <article key={item}>
                                        <strong>{String(index + 1).padStart(2, "0")}</strong>
                                        <span>{item}</span>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="animation3d-service-section space">
                    <div className="container">
                        <div className="animation3d-heading text-center">
                            <span>Blender Animation Services</span>
                            <h2>Game-Ready Animation Built Around Playable Moments</h2>
                            <p>We use Blender for animation creation, cleanup, polish, and export planning so game teams receive assets that are practical for production.</p>
                        </div>
                        <div className="animation3d-service-grid">
                            {data.services.map(([title, text]) => (
                                <article key={title}>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className={`animation3d-plan-section animation3d-plan-${data.layout} space`}>
                    <div className="container">
                        <div className="animation3d-split-heading">
                            <div className="animation3d-heading mb-0">
                                <span>Production Plan</span>
                                <h2>{data.tableTitle}</h2>
                            </div>
                            <p>Every animation category is planned around gameplay purpose, production usage, Blender scene setup, loop quality, and game-engine handoff.</p>
                        </div>
                        <div className="animation3d-plan-grid">
                            {data.table.map((row, index) => (
                                <article className="animation3d-plan-card" key={row[0]}>
                                    <div className="animation3d-plan-index">{String(index + 1).padStart(2, "0")}</div>
                                    <div className="animation3d-plan-body">
                                        <span>{row[2]}</span>
                                        <h3>{row[0]}</h3>
                                        <p>{row[1]}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="animation3d-pipeline-section space">
                    <div className="container">
                        <div className="animation3d-heading text-center">
                            <span>Blender Pipeline</span>
                            <h2>From Reference to Game Handoff</h2>
                        </div>
                        <div className="animation3d-pipeline-grid">
                            {data.pipeline.map((item, index) => (
                                <article key={item}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <span>{item}</span>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="animation3d-quality-section space">
                    <div className="container">
                        <div className="animation3d-split-heading">
                            <div className="animation3d-heading mb-0">
                                <span>Quality Checks</span>
                                <h2>Production Review Before Game Handoff</h2>
                            </div>
                            <p>Each page has its own animation review focus so the final Blender files, motion clips, and exported assets are easier for developers to implement and maintain.</p>
                        </div>
                        <div className="animation3d-quality-grid">
                            {data.qualityChecks.map((item, index) => (
                                <article key={item}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <span>{item}</span>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="animation3d-deliverable-section space">
                    <div className="container">
                        <div className="animation3d-deliverable-panel">
                            <div className="animation3d-heading mb-0">
                                <span>Deliverables</span>
                                <h2>Animation Assets Your Game Team Can Use</h2>
                                <p>Deliverables are scoped by the project, but we keep files, clips, naming, loops, and handoff notes practical for game implementation.</p>
                            </div>
                            <div className="animation3d-deliverable-list">
                                {data.deliverables.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="animation3d-faq-section space">
                    <div className="container">
                        <div className="animation3d-heading text-center">
                            <span>FAQ</span>
                            <h2>{data.eyebrow} Questions</h2>
                        </div>
                        <div className="animation3d-faq-grid">
                            {data.faqs.map(([question, answer]) => (
                                <article key={question}>
                                    <h3>{question}</h3>
                                    <p>{answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="animation3d-cta">
                    <div className="container">
                        <div className="animation3d-cta-box">
                            <div>
                                <span>{data.eyebrow}</span>
                                <h2>Need Blender animation for your game assets?</h2>
                                <p>Share your character, creature, prop, rig, mocap file, cinematic idea, or product animation need. We will help plan a practical Blender animation workflow.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Animation Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export const CharacterAnimation = () => <Animation3DPage page="character" />;
export const CreatureAnimation = () => <Animation3DPage page="creature" />;
export const RiggingSkinning = () => <Animation3DPage page="rigging" />;
export const FacialAnimation = () => <Animation3DPage page="facial" />;
export const MotionCaptureCleanup = () => <Animation3DPage page="mocap" />;
export const GameCinematics = () => <Animation3DPage page="cinematics" />;
export const ProductAnimations = () => <Animation3DPage page="product" />;
